import type { MaybeRefOrGetter } from 'vue';
import type { SignupPath, SignupTrack } from '~/constants/market';
import { buildSignupHref } from '~/utils/signupHref';
import { isLandingPath, resolveLandingPage, type LandingPage } from '~/utils/landing/landingPage';

/** Which landing the visitor is on: /uae is the UAE page, everything else International. */
export const useLandingPage = () => {
  const route = useRoute();
  const page = computed<LandingPage>(() => resolveLandingPage(route.path));
  const track = computed<SignupTrack>(() => (page.value === '/uae' ? 'uae' : 'int'));
  // /, /international and /uae (any locale).
  const isLandingPage = computed(() => isLandingPath(route.path));
  return { page, track, isLandingPage };
};

/**
 * Register link for landing CTAs: track from the page (or given), optional partner path and
 * redirect. The visitor's query is appended only after hydration, so the SWR-cached HTML of
 * / and /uae never carries one visitor's UTMs.
 */
export const useSignupHref = (
  options: {
    track?: MaybeRefOrGetter<SignupTrack | undefined>;
    path?: MaybeRefOrGetter<SignupPath | null | undefined>;
    redirect?: MaybeRefOrGetter<string | null | undefined>;
  } = {},
) => {
  const { track: pageTrack } = useLandingPage();
  const search = ref('');

  onMounted(() => {
    search.value = window.location.search;
  });

  const signupHref = computed(() =>
    buildSignupHref({
      track: toValue(options.track) ?? pageTrack.value,
      path: toValue(options.path) ?? null,
      redirect: toValue(options.redirect) ?? null,
      search: search.value,
    }),
  );

  return {
    signupHref,
  };
};
